import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Portal } from '@radix-ui/react-portal';
import {
  CreditCard,
  Smartphone,
  Landmark,
  Building2,
  Banknote,
  X,
  Download,
  CircleAlert,
  TriangleAlert,
} from 'lucide-react';
import { formatNumber } from 'helpers';
import KkiapayTrigger from 'components/shared/KkiapayTrigger';
import Modal from 'components/shared/Modal';
import { MOMO_MAX_AMOUNT } from 'data';
import Locales from 'locales';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Label } from 'components/ui/label';
import { Button } from 'components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import { Alert } from 'components/ui/alert';
import { downloadBankDepositPdf, getBankDepositDetails } from './bankDepositDocument';

const BANKS = [
  {
    id: 'ecobank',
    name: 'Ecobank',
    description: 'La banque panafricaine',
  },
  {
    id: 'biic',
    name: 'BIIC',
    description: "Banque Internationale pour l'Industrie et le Commerce",
  },
];

const METHOD_CARD = 'card';
const METHOD_DEPOSIT = 'bank_deposit';
const METHOD_MOBILE = 'mobile';

const METHODS = [
  {
    id: METHOD_CARD,
    name: 'Carte Bancaire',
    description: 'Visa/Mastercard',
    icon: CreditCard,
    tooltip: Locales.profile.payment.direct,
  },
  {
    id: METHOD_DEPOSIT,
    name: 'Dépôt Bancaire',
    description: 'Dépôt direct',
    icon: Banknote,
    tooltip: Locales.profile.payment.bank,
  },
  {
    id: METHOD_MOBILE,
    name: 'Mobile Money',
    description: 'MTN ou Moov',
    icon: Smartphone,
    tooltip: Locales.profile.payment.direct,
  },
];

const SelectPaymentMethod = ({
  isOpened = false,
  closeModal = () => {},
  invoice,
  agency,
  handleOpenKkiapay = () => {},
}) => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const selectedAmount = invoice.amount;
  const invoiceCode = invoice.code;
  const selectedBankDetails = BANKS.find((bank) => bank.id === selectedBank);
  const allowMOMO = selectedAmount <= MOMO_MAX_AMOUNT;
  const depositDetails = getBankDepositDetails({ bank: selectedBankDetails, amount: selectedAmount, invoiceCode });

  return (
    <Modal isOpen={isOpened} onOpenChange={closeModal}>
      <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
        <div className="space-y-3">
          <Label className="text-base font-semibold text-hajj-primary">
            {Locales.profile.select_payment_method.title}
          </Label>

          <div className="grid md:grid-cols-3 gap-4">
            {METHODS.map((method) => {
              const Icon = method.icon;
              return (
                <Tooltip key={method.id}>
                  <TooltipTrigger asChild>
                    <Card
                      className={`cursor-pointer transition-all ${
                        selectedPaymentMethod === method.id
                          ? 'border border-hajj-primary bg-hajj-muted'
                          : 'border border-muted hover:border-hajj-primary/50'
                      }`}
                      onClick={() => setSelectedPaymentMethod(method.id)}
                    >
                      <CardContent className="pt-6 text-center space-y-3 pb-6">
                        <div className="mx-auto w-12 h-12 bg-hajj-primary/10 rounded-full flex items-center justify-center">
                          <Icon className="w-6 h-6 text-hajj-primary" />
                        </div>
                        <div>
                          <p className="font-semibold">{method.name}</p>
                          <p className="text-sm text-muted-foreground">{method.description}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </TooltipTrigger>
                  <Portal>
                    <TooltipContent>
                      <p className="max-w-xs text-sm">{method.tooltip}</p>
                    </TooltipContent>
                  </Portal>
                </Tooltip>
              );
            })}
          </div>
        </div>

        {selectedPaymentMethod === METHOD_CARD && (
          <Card className="bg-hajj-muted border-hajj-primary/20">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                {Locales.profile.select_payment_method.card_title}
              </CardTitle>
              <CardDescription>{Locales.profile.select_payment_method.card_description}</CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              <Alert variant="outline" className="bg-white">
                <CircleAlert />
                <p>{Locales.profile.payment.direct}</p>
              </Alert>

              <div className="p-4 bg-white rounded-lg border border-border">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium">{Locales.profile.select_payment_method.amount_to_pay}</span>
                  <span className="text-xl font-bold text-hajj-primary">{`${formatNumber(selectedAmount)} FCFA`}</span>
                </div>

                <KkiapayTrigger
                  label={Locales.profile.select_payment_method.pay_by_card}
                  invoice={invoice}
                  agency={agency}
                  selectedAmount={selectedAmount}
                  method="card"
                  onOpen={handleOpenKkiapay}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {selectedPaymentMethod === METHOD_MOBILE && (
          <Card className="bg-hajj-muted border-hajj-primary/20">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Smartphone className="w-5 h-5" />
                {Locales.profile.select_payment_method.mobile_title}
              </CardTitle>
              <CardDescription>{Locales.profile.select_payment_method.mobile_description}</CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              {allowMOMO && (
                <>
                  <Alert variant="outline" className="bg-white">
                    <CircleAlert />
                    <p>{Locales.profile.payment.direct}</p>
                  </Alert>

                  <div className="p-4 bg-white rounded-lg border border-border">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium">{Locales.profile.select_payment_method.amount_to_pay}</span>
                      <span className="text-xl font-bold text-hajj-primary">{`${formatNumber(
                        selectedAmount
                      )} FCFA`}</span>
                    </div>

                    <KkiapayTrigger
                      label={Locales.profile.select_payment_method.pay_by_mobile}
                      invoice={invoice}
                      agency={agency}
                      selectedAmount={selectedAmount}
                      method="momo"
                      onOpen={handleOpenKkiapay}
                    />
                  </div>
                </>
              )}

              {!allowMOMO && (
                <Alert variant="outline" className="bg-white">
                  <TriangleAlert />
                  <p>
                    Le montant maximum autorisé pour Mobile Money est de{' '}
                    <strong>{formatNumber(MOMO_MAX_AMOUNT)} FCFA</strong>
                  </p>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Dépôt bancaire */}
        {selectedPaymentMethod === METHOD_DEPOSIT && (
          <Card className="bg-hajj-muted border-hajj-primary/20">
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between gap-2">
                <div className="flex gap-2">
                  <div className="flex items-center gap-2">
                    <Landmark className="w-5 h-5" />
                    {depositDetails.title}
                  </div>

                  {selectedBank && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-full border-hajj-primary/30 bg-white text-hajj-primary hover:bg-white hover:text-hajj-primary"
                          aria-label="Télécharger les instructions de dépôt"
                          onClick={() => downloadBankDepositPdf(depositDetails)}
                        >
                          <Download />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Télécharger</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>

                {selectedBank && (
                  <Button size="icon-sm" onClick={() => setSelectedBank(null)}>
                    <X />
                  </Button>
                )}
              </CardTitle>

              <CardDescription>
                {!selectedBank && <span>Sélectionner une banque pour effectuer le dépôt</span>}
                {selectedBank && <span>{depositDetails.instruction}</span>}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              {!selectedBank && (
                <>
                  <Alert variant="outline" className="bg-white">
                    <CircleAlert />
                    <p>{Locales.profile.payment.bank}</p>
                  </Alert>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {BANKS.map((bank) => (
                      <Card
                        key={bank.id}
                        className="cursor-pointer transition-all hover:shadow-lg hover:scale-105 hover:border-hajj-primary/50 border border-muted bg-white"
                        onClick={() => setSelectedBank(bank.id)}
                      >
                        <CardContent className="pt-6 space-y-3">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-hajj-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Building2 className="w-6 h-6 text-hajj-primary" />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-[22px] text-base">{bank.name}</h3>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </>
              )}

              {selectedBank && selectedBankDetails && (
                <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
                  <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-hajj-primary/10 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-hajj-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{depositDetails.bankName}</p>
                        <p className="text-xs text-muted-foreground">{depositDetails.bankDescription}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">{depositDetails.amountLabel}</Label>
                      <div className="p-3 bg-white rounded-md text-lg font-bold border border-border">
                        {depositDetails.amount}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">{depositDetails.invoiceLabel}</Label>
                      <div className="p-3 bg-white rounded-md text-lg font-bold border border-border">
                        {depositDetails.invoiceCode}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Modal>
  );
};

SelectPaymentMethod.propTypes = {
  invoice: PropTypes.shape({}).isRequired,
  agency: PropTypes.shape({}).isRequired,
  isOpened: PropTypes.bool,
  closeModal: PropTypes.func,
  handleOpenKkiapay: PropTypes.func,
};

export default SelectPaymentMethod;
